import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { NgxCurrencyInputMode } from 'ngx-currency';
import { environment } from 'environments/environment';

export type SupportedLanguage = 'pt-br' | 'pt' | 'en' | 'es' | 'fr' | 'de' | 'zh' | 'ru' | 'ja' | 'ar';
export type LayoutDirection = 'ltr' | 'rtl';

export interface LanguageOption {
    code: SupportedLanguage;
    name: string;
    flag: string; // Emoji para fallback
    flagUrl: string; // URL da flag SVG
}

/**
 * Lista de idiomas disponíveis no sistema
 */
export const AVAILABLE_LANGUAGES: LanguageOption[] = [
    { code: 'pt-br', name: 'Português (BR)', flag: '🇧🇷', flagUrl: 'assets/images/flags/BR.svg' },
    { code: 'pt', name: 'Português (PT)', flag: '🇵🇹', flagUrl: 'assets/images/flags/PT.svg' },
    { code: 'en', name: 'English', flag: '🇺🇸', flagUrl: 'assets/images/flags/US.svg' },
    { code: 'es', name: 'Español', flag: '🇪🇸', flagUrl: 'assets/images/flags/ES.svg' },
    { code: 'fr', name: 'Français', flag: '🇫🇷', flagUrl: 'assets/images/flags/FR.svg' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪', flagUrl: 'assets/images/flags/DE.svg' },
    { code: 'zh', name: '中文', flag: '🇨🇳', flagUrl: 'assets/images/flags/CN.svg' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺', flagUrl: 'assets/images/flags/RU.svg' },
    { code: 'ja', name: '日本語', flag: '🇯🇵', flagUrl: 'assets/images/flags/JP.svg' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦', flagUrl: 'assets/images/flags/SA.svg' },
];

@Injectable({
    providedIn: 'root',
})
export class LanguageService {
    private readonly STORAGE_KEY = 'prehox_selected_language';
    private currentLanguage$ = new BehaviorSubject<SupportedLanguage>(
        this.getStoredLanguage() || (environment.defaultLanguage as SupportedLanguage) || 'pt-br',
    );

    constructor() {
        // Carregar idioma salvo do LocalStorage
        const stored = this.getStoredLanguage();
        if (stored) {
            this.currentLanguage$.next(stored);
        }
    }

    /**
     * Obtém o idioma atual
     */
    getCurrentLanguage(): SupportedLanguage {
        return this.currentLanguage$.value;
    }

    /**
     * Observable do idioma atual
     */
    getLanguage$(): Observable<SupportedLanguage> {
        return this.currentLanguage$.asObservable();
    }

    /** Retorna true para idiomas cuja escrita flui da direita para a esquerda. */
    isRtl(language: SupportedLanguage = this.getCurrentLanguage()): boolean {
        return language === 'ar';
    }

    /** Direção visual usada pelo documento e pelo Angular CDK. */
    getDirection(language: SupportedLanguage = this.getCurrentLanguage()): LayoutDirection {
        return this.isRtl(language) ? 'rtl' : 'ltr';
    }

    /** Código BCP 47 aplicado no atributo lang do documento. */
    getDocumentLanguageCode(language: SupportedLanguage = this.getCurrentLanguage()): string {
        return language === 'pt-br' ? 'pt-BR' : language;
    }

    /**
     * Define o idioma atual
     */
    setLanguage(language: SupportedLanguage): void {
        if (this.currentLanguage$.value !== language) {
            this.currentLanguage$.next(language);
            this.saveLanguage(language);
        }
    }

    /**
     * Se o utilizador nunca escolheu idioma localmente e o perfil no servidor tem preferência, aplica-a.
     */
    applyPreferredLanguageFromServerUser(user: { preferred_ui_language?: string | null } | null): void {
        if (!user?.preferred_ui_language) {
            return;
        }
        const code = user.preferred_ui_language as SupportedLanguage;
        if (!AVAILABLE_LANGUAGES.some((l) => l.code === code)) {
            return;
        }
        if (typeof window === 'undefined' || !window.localStorage) {
            return;
        }
        if (localStorage.getItem(this.STORAGE_KEY) != null) {
            return;
        }
        this.setLanguage(code);
    }

    /**
     * Obtém o idioma salvo no LocalStorage
     */
    private getStoredLanguage(): SupportedLanguage | null {
        if (typeof window === 'undefined' || !window.localStorage) {
            return null;
        }
        try {
            const stored = localStorage.getItem(this.STORAGE_KEY);
            const validCodes = AVAILABLE_LANGUAGES.map((lang) => lang.code);
            if (stored && validCodes.includes(stored as SupportedLanguage)) {
                return stored as SupportedLanguage;
            }
        } catch (error) {
            console.warn('Erro ao ler idioma do LocalStorage:', error);
        }
        return null;
    }

    /**
     * Salva o idioma no LocalStorage
     */
    private saveLanguage(language: SupportedLanguage): void {
        if (typeof window === 'undefined' || !window.localStorage) {
            return;
        }
        try {
            localStorage.setItem(this.STORAGE_KEY, language);
        } catch (error) {
            console.warn('Erro ao salvar idioma no LocalStorage:', error);
        }
    }

    /**
     * Converte código de idioma para código da API
     * Mapeia códigos internos para códigos suportados pela API LibreTranslate
     */
    getApiLanguageCode(language?: SupportedLanguage): string {
        const lang = language || this.getCurrentLanguage();

        // Mapeamento de códigos internos para códigos da API
        const apiCodeMap: Record<SupportedLanguage, string> = {
            'pt-br': 'pt-br',
            pt: 'pt',
            en: 'en',
            es: 'es',
            fr: 'fr',
            de: 'de',
            zh: 'zh', // Chinês (Mandarim)
            ru: 'ru',
            ja: 'ja',
            ar: 'ar',
        };

        return apiCodeMap[lang] || lang;
    }

    /** Moeda de exibição: EUR em Português (pt), BRL caso contrário. */
    getDisplayCurrencyCode(): 'BRL' | 'EUR' {
        return this.getCurrentLanguage() === 'pt' ? 'EUR' : 'BRL';
    }

    /** Locale para Intl / CurrencyPipe alinhado à moeda de exibição. */
    getDisplayCurrencyLocale(): 'pt' | 'pt-BR' {
        return this.getCurrentLanguage() === 'pt' ? 'pt' : 'pt-BR';
    }

    /** Sufixo para títulos que mencionam a moeda explicitamente, ex. (R$) vs (EUR). */
    getCurrencySymbolInParens(): string {
        return this.getCurrentLanguage() === 'pt' ? '(EUR)' : '(R$)';
    }

    /** Formata valor monetário para o idioma atual (BRL ou EUR). */
    formatDisplayCurrency(value: number): string {
        return new Intl.NumberFormat(this.getDisplayCurrencyLocale(), {
            style: 'currency',
            currency: this.getDisplayCurrencyCode(),
        }).format(value);
    }

    /**
     * Opções para [options] do ngx-currency (valor médio hora etc.): R$ ou € conforme idioma.
     */
    getNgxCurrencyMaskOptions(): {
        align: string;
        allowNegative: boolean;
        allowZero: boolean;
        decimal: string;
        thousands: string;
        precision: number;
        prefix: string;
        suffix: string;
        nullable: boolean;
        min: number | null;
        max: number | null;
        inputMode: NgxCurrencyInputMode;
    } {
        return {
            align: 'left',
            allowNegative: false,
            allowZero: true,
            decimal: ',',
            thousands: '.',
            precision: 2,
            prefix: this.getCurrentLanguage() === 'pt' ? '€ ' : 'R$ ',
            suffix: '',
            nullable: false,
            min: 0,
            max: null,
            inputMode: NgxCurrencyInputMode.Financial,
        };
    }
}
